import React, {
	useEffect,
	useState,
	forwardRef,
	FocusEvent,
	InputHTMLAttributes,
	ReactElement,
	useCallback,
	useRef,
} from 'react';
import Label from '../Label';
import './InputBase.css';
import { IMaskInput } from 'react-imask';
import { getSubtext, getRequiredMessage } from '../../utils/inputUtils';
import SuccessIcon from './SuccessIcon/SuccessIcon';
import { addUnderscores } from '../../utils/utils';
import showIcon from '../../assets/icon-show.svg';
import hideIcon from '../../assets/icon-hide.svg';
import DynamicContainer from '../../foundational/Layout/DynamicContainer';

export interface IInputProps {
	mask?: string | Record<string,any>[];
	lazy?: boolean;
	eager?: boolean;
	setIsVisible?: React.Dispatch<React.SetStateAction<boolean>>;
	isVisible?: boolean;
	displayChar?: string;
	className?: string;
	labelClass?: string;
	inputClass?: string;
	type?: string;
	name?: string;
	id?: string;
	required?: boolean;
	requiredMessage?: string;
	removeAsterisk?: boolean;
	label?: string | ReactElement | ReactElement[];
	helperText?: string | ReactElement | ReactElement[];
	errorText?: string;
	value: any;
	disabled?: boolean;
	error?: boolean;
	setValue: THandlerFunction;
	onChange?: THandlerFunction;
	onPaste?: THandlerFunction;
	description?: string | boolean | ReactElement | ReactElement[] | undefined;
	labelAlign?: 'left' | 'center';
	tooltip?: string | ReactElement | ReactElement[];
	tooltipNotchPosition?:
	| 'bottom-center'
	| 'bottom-left'
	| 'bottom-right'
	| 'top-center'
	| 'top-left'
	| 'top-right';
	advanced?: boolean;
	placeholder?: string;
	onBlur?: (value: FocusEvent<HTMLInputElement>) => void;
	onFocus?: (value: FocusEvent<HTMLInputElement>) => void;
	mobileTooltipNotchPosition?:
	| 'bottom-center'
	| 'bottom-left'
	| 'bottom-right'
	| 'top-center'
	| 'top-left'
	| 'top-right'
	| null;
	minLength?: number | undefined;
	maxLength?: number | undefined;
	hideAlertIcon?: boolean;
	isValid?: (val: string | number) => boolean;
	formatValue?: (val: string | number) => string | number;
	invalidErrorMessage?: string;
	valid?: [boolean, (arg?: any) => void];
	externalValidation?: boolean;
	noErrorsOrHelper?: boolean;
	inputChild?: ReactElement | undefined;
	alternate?: boolean;
	showSearchIcon?: boolean;
	readOnly?: boolean;
	showSuccessIcon?: boolean;
	useSuccessIcon?: boolean;
	// this can be removed once all AEM inputs have been updated (and their related css removed) 
	// if we want to use this icon by default
	showExclamationError?: boolean;
	containerId?: string;
}

const InputBase = forwardRef(
	(
		{
			className = '',
			labelClass = '',
			inputClass = '',
			type = 'text',
			name,
			id = '',
			required = false,
			requiredMessage = '',
			removeAsterisk = false,
			label,
			helperText = '',
			errorText = '',
			value,
			disabled = false,
			error = false,
			setValue = () => { },
			onChange = () => { },
			description = '',
			labelAlign = 'left',
			tooltip = '',
			tooltipNotchPosition = 'bottom-left',
			advanced = false,
			placeholder = '',
			onBlur = () => { },
			onFocus = () => { },
			mobileTooltipNotchPosition = null,
			minLength = undefined,
			maxLength = undefined,
			hideAlertIcon = false,
			isValid = () => {
				return true;
			},
			formatValue = (val: string | number) => {
				return val;
			},
			invalidErrorMessage = '',
			valid = [true, () => { }],
			externalValidation = false,
			noErrorsOrHelper = false,
			inputChild = undefined,
			alternate = false,
			showSearchIcon = false,
			readOnly = false,
			mask,
			eager = false,
			lazy = true,
			showSuccessIcon = false,
			useSuccessIcon = false,
			showExclamationError = false,
			containerId,
			...rest
		}: IInputProps & InputHTMLAttributes<any>,
		ref: any,
	): JSX.Element => {
		const [focused, setFocused] = useState(false);
		const [validationError, setValidationError] = useState(false);
		const [errorMessage, setErrorMessage] = useState('');
		const ariaLabel = 'aria-label' in rest ? rest['aria-label'] : '';
		const showHideRef = useRef<any>();

		if (id === name) {
			console.error(
				`Props 'id' and 'name for the InputBase Component should be different.`,
			);
		}
		if (errorText !== '' && error === undefined) {
			console.error(
				`Prop "errorText" requires an error prop to be passed to Input to display the errorText.`,
			);
		}

		if (maxLength && minLength && maxLength < minLength) {
			console.error('maxLength cannot be less than minLength');
		}

		const isValidInternally = (
			val: string | number,
			focussed: boolean,
		): boolean => {
			if (externalValidation) return true;
			if (!formatValue(val) && required) {
				setErrorMessage(
					requiredMessage || getRequiredMessage({ label }),
				);
				return false;
			}
			if (
				!focussed &&
				minLength &&
				formatValue(val) &&
				formatValue(val).toString().length < minLength
			) {
				setErrorMessage(
					invalidErrorMessage ||
					`${label} should have at least ${minLength} characters.`,
				);
				return false;
			}
			if (!focussed && formatValue(val) && !isValid(formatValue(val))) {
				setErrorMessage(
					invalidErrorMessage || `Please provide a valid ${label}`,
				);
				return false;
			}
			if (focussed && validationError) {
				if (
					errorMessage === requiredMessage ||
					(`${label} is required.` && formatValue(val))
				) {
					return true;
				}
				return isValid(formatValue(val));
			}
			return true;
		};

		const ShowHideIcon = useCallback(() => {
			return (
				<button ref={showHideRef}
					// required to ensure form doesn't treat this like submit
					type='button'
					id='showHideToggle'
					className='!p-2'
					// interupts focus change on click so user is still focused on input
					onMouseDown={(e: React.MouseEvent) => e.preventDefault()}
					// handles click, space, enter
					onClick={(e: React.MouseEvent) => {
						e.preventDefault();
						rest.setIsVisible && rest.setIsVisible(prev => !prev);
						if (document.activeElement === showHideRef?.current) {
							setTimeout(() => showHideRef?.current?.focus(), 0);
						}
					}}
					// force blur logic when focusing away from both input and toggle
					onBlur={(e) => {
						if (e.relatedTarget !== ref.current) {
							ref.current.dispatchEvent(new Event('blur'));
						}
					}}
					aria-label={rest.isVisible ? 'Click to mask the value' : 'Click to unmask the value'}>
					{rest.isVisible ? <img title='Click to mask the value' src={showIcon} /> : <img title='Click to unmask the value' src={hideIcon} />}
				</button>
			);
		}, [rest.isVisible]);

		useEffect(() => {
			// When values are assigned without interaction with the inputs
			if (!externalValidation && !focused && value) {
				if (
					minLength &&
					formatValue(value).toString().length < minLength
				) {
					setErrorMessage(
						invalidErrorMessage ||
						`${label} should have at least ${minLength} characters.`,
					);
					setValidationError(true);
				} else if (!isValid(formatValue(value))) {
					setErrorMessage(
						invalidErrorMessage || getRequiredMessage({ label }),
					);
					setValidationError(true);
				} else {
					setValidationError(false);
				}
			}
		}, [value]);

		useEffect(() => {
			if (error) {
				isValidInternally(value, focused);
			}
		}, [
			invalidErrorMessage,
			requiredMessage
		]);

		return (	
			<DynamicContainer displayTag={useSuccessIcon} tag='div' className='InputBase__container' {...(containerId && { id :containerId})}>
				<label
					htmlFor={name}
					className={`InputBase ${className} ${disabled ? 'InputBase--disabled' : ''
						}`}
					data-cl='10'
				>
					{!advanced && label && (
						<Label
							error={error || validationError}
							helperText={helperText}
							className={labelClass}
							tooltip={tooltip}
							tooltipAlign={tooltipNotchPosition}
							mobileTooltipAlign={mobileTooltipNotchPosition}
							required={removeAsterisk ? false : required}
							alternate={alternate}
						>
							{label}
						</Label>
					)}
					<div
						className={`${!alternate ? 'Legacy-' : ''
							}InputBase__inputWrapper${readOnly
								? '--readOnly'
								: error || validationError
									? '--error'
									: disabled
										? '--disabled'
										: ''
							} ${className}`}
					>
						{mask ? (
							<>
								<IMaskInput
									{...rest}
									displayChar={rest.isVisible ? undefined : rest.displayChar}
									eager={eager}
									mask={mask as string}
									lazy={lazy}
									value={value}
									type={type}
									name={name}
									id={id}
									inputRef={ref}
									disabled={disabled}
									readOnly={readOnly}
									className={`${!alternate ? 'Legacy-' : ''
										}InputBase__input${showSearchIcon
											? '--search'
											: readOnly
												? '--readOnly'
												: ''
										} ${inputClass} ${(error || validationError) && alternate
											? 'InputBase__input--error'
											: ''
										}`}
									onAccept={(_, { unmaskedValue }, event) => {
										const refVal = ref.current.value
										const masked = refVal.slice(-1) === rest.displayChar
										if (validationError && !masked) {
											setValidationError(
												!isValidInternally(
													unmaskedValue,
													true,
												),
											);
										}									
										setValue(unmaskedValue);
										onChange(ref.current);
										// handle cursor specifically for SSN when eagerly adding formatting characters
										if (
											ref.current &&
											mask.at(-1)?.['mask'] === 'XXX-XX-XXX0' && // ssn only
											ref.current.selectionStart === ref.current.selectionEnd &&
											event?.inputType === 'insertText' &&
											[3, 6].includes(ref.current.selectionStart)
										) {
											setTimeout(() => {
												ref?.current?.setSelectionRange(ref.current.value.length, ref.current.value.length);
											}, 0);
										}
									}}
									onBlur={(e) => {
										// do not trigger blur logic when focusing on show/hide toggle
										if (e.relatedTarget === showHideRef?.current) {
											return;
										}

										setFocused(false);
										setValidationError(
											!isValidInternally(value, false),
										);
										if (!externalValidation && valid && typeof valid[1] === 'function') {
											valid[1](
												isValidInternally(
													value,
													false,
												),
											);
										}
										if (onBlur) {
											onBlur(e);
										}
									}}
									onFocus={(e) => {
										setFocused(true);
										if (onFocus) {
											onFocus(e);
										}
									}}
									placeholder={
										!advanced && placeholder ? placeholder : ''
									}
									minLength={minLength}
									aria-required={required || undefined}
									aria-disabled={disabled || undefined}
									aria-describedby={id || name}
									aria-label={
										typeof label === 'string' && ariaLabel === ''
											? label
											: ariaLabel
									}
									aria-invalid={validationError || error || undefined}
									aria-errormessage={errorMessage || errorText || undefined}
								/>
								{rest.setIsVisible && <ShowHideIcon />}
							</>
						) : (
							<input
								{...rest}
								value={value}
								type={type}
								name={name}
								id={id}
								ref={ref}
								disabled={disabled}
								readOnly={readOnly}
								className={`${!alternate ? 'Legacy-' : ''
									}InputBase__input${showSearchIcon
										? '--search'
										: readOnly
											? '--readOnly'
											: ''
									} ${inputClass} ${(error || validationError) && alternate
										? 'InputBase__input--error'
										: ''
									}`}
								onChange={(e) => {
									if (validationError) {
										setValidationError(
											!isValidInternally(
												e.target.value,
												true,
											),
										);
									}
									setValue(e.target.value);
									onChange(e.target.value);
								}}
								onBlur={(e) => {
									setFocused(false);
									setValidationError(
										!isValidInternally(e.target.value, false),
									);
									if (!externalValidation && valid && typeof valid[1] === 'function') {
										valid[1](
											isValidInternally(
												e.target.value,
												false,
											),
										);
									}
									if (onBlur) {
										onBlur(e);
									}
								}}
								onFocus={(e) => {
									setFocused(true);
									if (onFocus) {
										onFocus(e);
									}
								}}
								placeholder={
									!advanced && placeholder ? placeholder : ''
								}
								minLength={minLength}
								maxLength={maxLength}
								aria-required={required  || undefined}
								aria-disabled={disabled  || undefined}
								aria-describedby={id || name}
								aria-label={
									typeof label === 'string' && ariaLabel === ''
										? label
										: ariaLabel
								}
								aria-invalid={validationError || error || undefined}
								aria-errormessage={errorMessage || errorText  || undefined}
								autoComplete={
									id?.toLowerCase()?.includes('city') || label?.toString()?.toLowerCase()?.includes('city') ? 
									'address-level2' : 
									undefined
								}
							/>
						)}
						{inputChild && inputChild}
					</div>
					{!noErrorsOrHelper ? (
						<p
							className={`${!alternate ? 'Legacy-' : ''
								}InputBase__description${error || validationError ? '--error' : ''
								}${showExclamationError ? 'WithExclamation': ''}`}
							id={`${addUnderscores(id, name)}__input__adv__helper`}
							aria-live='assertive'
							tabIndex={-1}
						>
							{getSubtext({
								validationError,
								errorMessage,
								error,
								errorText,
								advanced,
								helperText,
								description,
							})}
						</p>
					) : (
						description && (
							<p
								className={`${!alternate ? 'Legacy-' : ''
									}InputBase__description`}
								id={`${addUnderscores(id, name)}__input__adv__helper`}
								aria-live='assertive'
							>
								{description}
							</p>
						)
					)}
				</label>
				{useSuccessIcon && 
				<SuccessIcon showIcon={showSuccessIcon && (!focused || disabled) && value}/>}
			</DynamicContainer>
		);
	},
);

InputBase.displayName = 'InputBase';

export default InputBase;
